import { APIStatus } from "@/api/APIStatus.type";
import clsx from "clsx";
import { useEffect, useMemo, useState } from "react";
import { Oval } from "react-loading-icons";
import { useSelector } from "react-redux";

import { playerSearchWithMetaRatings } from "../../../../../api/apiFc24";
import { getPlayerRoleLabel } from "../../../../../functions/getPlayerRoleLabel";
import { useOutsideClick } from "../../../../../hooks/useOutsideClick";
import useSearch from "../../../../../hooks/useSearch";
import { PlayerRole } from "../../../../../redux/players/playersSlice";
import {
  PlayerToInclude,
  SquadBuilderMode,
  SquadBuilderPlayerType,
} from "../../../../../redux/squadBuilder/squadBuilderUtils";
import { getUserSelector } from "../../../../../redux/user/userSlice";
import { Input } from "../../../../atoms/InputField/Input";
import { SquadBuilderPlayer } from "../../../../molecules/Player/SquadBuilderPlayer/SquadBuilderPlayer";
import { SortByMetaRatingOrChemistry } from "../../../../molecules/SortByMetaRatingOrChemistry/SortByMetaRatingOrChemistry";
import { ComponentContainer } from "../../components/ComponentContainer/ComponentContainer";
import { PanelContainer } from "../../components/PanelContainer/PanelContainer";
import { PanelHeader } from "../PanelHeader/PanelHeader";

type Props = {
  mode: SquadBuilderMode;
  handleClickedClose: () => void;
  handleDidAddPlayer: (
    playerRoleId: number,
    player: SquadBuilderPlayerType,
    temp?: boolean,
  ) => void;
  handleRemoveTempPlayer?: () => void;
  handleGetPlayerRecommendations: () => void;
  recommendedPlayers: SquadBuilderPlayerType[];
  playerRoles: PlayerRole[];
  playersToInclude?: PlayerToInclude[];
  changingPitchItemOnIndex?: number;
  availablePlayerRoles?: Record<string, boolean>;
  pitchIsFull?: boolean;
  statusGetRecommendedPlayers?: APIStatus;
  playersOnPitch: SquadBuilderPlayerType[];
  playersOnBench: SquadBuilderPlayerType[];
  playerRoleId: number;
  tabletOrLargerMode?: boolean;
  desktopMode?: boolean;
  formationPosition?: string;
};

export const PlayerMode = ({
  mode,
  handleClickedClose,
  handleDidAddPlayer,
  handleRemoveTempPlayer,
  handleGetPlayerRecommendations,
  recommendedPlayers,
  playersToInclude,
  changingPitchItemOnIndex,
  availablePlayerRoles,
  pitchIsFull,
  statusGetRecommendedPlayers,
  playerRoles,
  playersOnPitch,
  playersOnBench,
  playerRoleId,
  tabletOrLargerMode,
  formationPosition,
}: Props) => {
  const ref = useOutsideClick(() => {
    handleClickedClose();
  });
  const [sortBy, setSortBy] = useState<"meta-rating" | "chemistry">(
    "meta-rating",
  );
  const playerRoleName = useMemo(() => {
    return playerRoles.find((playerRole) => playerRole.id === playerRoleId)
      ?.name;
  }, [playerRoles, playerRoleId]);
  const { user } = useSelector(getUserSelector);
  const [searchTerm, setSearchTerm, isSearching, result] = useSearch(
    "",
    (name: string, page: number) =>
      playerSearchWithMetaRatings(
        name,
        page,
        user.platform || "Playstation",
        playerRoleId ||
          playersToInclude[changingPitchItemOnIndex]?.playerRoleId,
      ),
  ) as [
    string,
    React.Dispatch<React.SetStateAction<string>>,
    boolean,
    SquadBuilderPlayerType[],
  ];
  const [players, setPlayers] = useState<SquadBuilderPlayerType[]>([]);
  const [noPlayersFound, setNoPlayersFound] = useState(false);
  const loading = statusGetRecommendedPlayers === "pending" || isSearching;

  useEffect(() => {
    let tempPlayers = result || recommendedPlayers || [];
    tempPlayers = [...tempPlayers]?.sort((a, b) => {
      if (sortBy === "meta-rating") {
        return b.teamMetaRating - a.teamMetaRating;
      } else {
        if (a.teamChemistry !== b.teamChemistry) {
          return b.teamChemistry - a.teamChemistry;
        }
        return b.teamMetaRating - a.teamMetaRating;
      }
    });
    setPlayers(tempPlayers);
    setNoPlayersFound(
      tempPlayers.length === 0 && statusGetRecommendedPlayers === "fulfilled",
    );
  }, [result, recommendedPlayers, sortBy, playersToInclude, mode]);

  useEffect(() => {
    if (changingPitchItemOnIndex >= 0) {
      handleGetPlayerRecommendations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changingPitchItemOnIndex]);

  return (
    <div ref={ref} className="flex flex-col">
      <PanelHeader title={"Add Player"} onClose={handleClickedClose} />
      <PanelContainer>
        <ComponentContainer marginTop="mt-8 touch-device:mt-6">
          <div className="flex flex-col w-full mx-auto">
            <span className="h-5 pl-2 text-xs text-secondary">
              Player Search
            </span>
            <Input
              placeholder="Search for player"
              onChange={setSearchTerm}
              autofocus={tabletOrLargerMode}
            >
              {searchTerm}
            </Input>
          </div>
        </ComponentContainer>
        <div className="flex flex-col w-full mt-2 gap-y-1 touch-device:mt-7">
          {(!isSearching || result?.length === 0) && (
            <>
              <p className="ml-2 text-xs text-secondary">
                AI Suggestions for {playerRoleName} ✨
              </p>
              <SortByMetaRatingOrChemistry
                sortBy={sortBy}
                onChange={setSortBy}
                disableSortByChemistry={mode === "best-formation"}
              />
            </>
          )}
          {loading ? (
            <div className="mx-auto mt-8">
              <Oval width={36} height={36} />
            </div>
          ) : noPlayersFound && statusGetRecommendedPlayers === "fulfilled" ? (
            <p className="mx-auto mt-2 text-xs text-secondary px-4">
              No {playerRoleName ?? "player"}'s found with the current filters
            </p>
          ) : (
            <div className="grid items-center justify-center grid-cols-2 mobile:mt-2 ml-4 mt-24">
              {players.map((player) => {
                const mappedPlayerRole =
                  playerRoleId ||
                  playersToInclude[changingPitchItemOnIndex]?.playerRoleId ||
                  player.metaRatings[0]?.playerRoleId;

                const playerCannotBeChosen =
                  ((pitchIsFull || !availablePlayerRoles?.[mappedPlayerRole]) &&
                    changingPitchItemOnIndex < 0) ||
                  (player.preferredPosition !== "GK" &&
                    changingPitchItemOnIndex === 0) ||
                  playersOnPitch.some(
                    (p) => p.assetId && p.assetId === player.assetId,
                  ) ||
                  playersOnBench.some(
                    (p) => p.assetId && p.assetId === player.assetId,
                  );

                return (
                  <div
                    key={
                      player.resourceId +
                      ("" + player?.eaPlayerId) +
                      ("" + player?.evoPathId)
                    }
                    className={clsx(
                      "relative sb-v2-lg:mt-0 cursor-pointer",
                      playerCannotBeChosen &&
                        "brightness-50 cursor-not-allowed",
                      !tabletOrLargerMode
                        ? "scale-[190%] h-[210px] mt-1 w-[80px] left-1/4"
                        : "scale-[90%] sb-v2-lg:scale-[100%] -left-2",
                    )}
                    onMouseEnter={() => {
                      !playerCannotBeChosen &&
                        tabletOrLargerMode &&
                        handleDidAddPlayer(mappedPlayerRole, player, true);
                    }}
                    onMouseLeave={() =>
                      tabletOrLargerMode && handleRemoveTempPlayer()
                    }
                  >
                    <SquadBuilderPlayer
                      player={{
                        ...player,
                      }}
                      playerRoleId={mappedPlayerRole}
                      position={
                        player.possiblePositions.includes(formationPosition)
                          ? formationPosition
                          : player.possiblePositions.includes(player.position)
                            ? player.position
                            : player.preferredPosition
                      }
                      formationPosition={""}
                      chemStyleId={0}
                      {...(player.chemistry >= 0 && {
                        chemistry: {
                          currentChemistry: player.chemistry,
                          previousChemistry: player.chemistry,
                          isOutOfPosition: false,
                        },
                      })}
                      metaRating={
                        player.metaRatings.length === 0
                          ? 0
                          : player.metaRatings?.[player.chemistry ?? 0]
                              .metaRating
                      }
                      metaRatingZeroChem={player.metaRatings[0]?.metaRating}
                      tabletOrLargerMode={tabletOrLargerMode}
                      onClickPlayer={(e) => {
                        !playerCannotBeChosen &&
                          handleDidAddPlayer(mappedPlayerRole, player, false);
                        e.stopPropagation();
                      }}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </PanelContainer>
    </div>
  );
};
